import { BaseComponent } from '../core/BaseComponent';

export interface Command {
  title: string;
  description?: string;
  shortcut?: string;
  action: () => void;
}

export interface CommandPaletteOptions {
  shortcut: string;
  placeholder: string;
  commands: Command[];
}

export class CommandPalette extends BaseComponent<CommandPaletteOptions> {
  private palette: HTMLElement | null = null;
  private input: HTMLInputElement | null = null;
  private results: HTMLElement | null = null;

  protected getDefaultOptions(): CommandPaletteOptions {
    return {
      shortcut: 'k',
      placeholder: 'Type a command or search...',
      commands: []
    };
  }

  protected init(): void {
    super.init();
    this.createDom();
  }

  private createDom(): void {
    this.palette = document.createElement('div');
    this.palette.className = 'command-palette';
    this.palette.innerHTML = `
      <div class="command-backdrop"></div>
      <div class="command-dialog" role="combobox" aria-haspopup="listbox" aria-expanded="false">
        <input type="text" class="command-input" placeholder="${this.options.placeholder}" aria-autocomplete="list">
        <div class="command-results" role="listbox"></div>
      </div>
    `;
    document.body.appendChild(this.palette);
    this.input = this.palette.querySelector('.command-input');
    this.results = this.palette.querySelector('.command-results');
  }

  protected bindEvents(): void {
    window.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === this.options.shortcut) {
        e.preventDefault();
        this.toggle();
      }
    });

    this.palette?.querySelector('.command-backdrop')?.addEventListener('click', () => this.hide());
    this.input?.addEventListener('input', () => this.search());
    
    this.palette?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.hide();
    });
  }

  public show(): void {
    if (!this.palette || !this.input) return;
    this.palette.classList.add('show');
    this.input.value = '';
    this.renderResults(this.options.commands);
    setTimeout(() => this.input?.focus(), 50);
    this.emit('show');
  }

  public hide(): void {
    this.palette?.classList.remove('show');
    this.emit('hide');
  }

  public toggle(): void {
    this.palette?.classList.contains('show') ? this.hide() : this.show();
  }

  private search(): void {
    if (!this.input) return;
    const query = this.input.value.toLowerCase();
    const filtered = this.options.commands.filter(cmd => 
      cmd.title.toLowerCase().includes(query) || 
      cmd.description?.toLowerCase().includes(query)
    );
    this.renderResults(filtered);
  }

  private renderResults(commands: Command[]): void {
    if (!this.results) return;
    this.results.innerHTML = '';
    
    if (commands.length === 0) {
      this.results.innerHTML = '<div class="command-empty">No results found</div>';
      return;
    }

    commands.forEach((cmd, idx) => {
      const el = document.createElement('div');
      el.className = 'command-item';
      el.setAttribute('role', 'option');
      el.id = `cmd-opt-${idx}`;
      el.innerHTML = `
        <div class="command-content">
          <div class="command-title">${cmd.title}</div>
          <div class="command-description">${cmd.description || ''}</div>
        </div>
        ${cmd.shortcut ? `<div class="command-shortcut">${cmd.shortcut}</div>` : ''}
      `;
      el.addEventListener('click', () => {
        cmd.action();
        this.hide();
      });
      this.results?.appendChild(el);
    });
  }
}
